import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDemo } from "@/store/DemoStore";
import { btn } from "@/components/common";
import { PlanFields, defaultPlan, planError } from "@/components/PaymentPlanEditor";
import { addDays, daysBetween, todayISO } from "@/lib/trip";
import { cn } from "@/lib/utils";

const DEFAULT_COVER = "/images/sunset.jpg";

function compressImage(file, max = 1280, quality = 0.72) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("load failed")); };
    img.src = url;
  });
}

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";

function F({ label, children, className }) {
  return <label className={className}><span className="mb-1.5 block text-xs font-medium text-slate-500">{label}</span>{children}</label>;
}

const sample = (clientId) => ({ clientId, title: "", bookingNumber: "", destination: "", kind: "cruise", supplier: "", vessel: "", room: "", departure: "", ret: "", travelers: 1, image: DEFAULT_COVER, plan: defaultPlan("") });

const fromTrip = (t) => ({ clientId: t.clientId, title: t.title, bookingNumber: t.bookingNumber || "", destination: t.destination, kind: t.kind, supplier: t.supplier, vessel: t.vessel, room: t.room, departure: t.departure, ret: addDays(t.departure, t.nights), travelers: t.travelers.length, image: t.image, plan: t.plan });

const fitDays = (days, n, place) => (days && days.length ? Array.from({ length: n + 1 }, (_, i) => days[i] || { title: "Day at leisure", place, items: [] }) : days);

export function NewQuoteDialog({ open, onOpenChange, defaultClient, trip }) {
  const { clients, client, createQuote, updateTrip } = useDemo();
  const [f, setF] = useState(() => (trip ? fromTrip(trip) : sample(defaultClient || clients[0]?.id || "")));
  const coverRef = useRef(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const nav = useNavigate();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setDeparture = (e) => { const departure = e.target.value; setF({ ...f, departure, ret: f.ret && f.ret <= departure ? "" : f.ret, plan: trip ? f.plan : { ...f.plan, finalDue: defaultPlan(departure).finalDue } }); };
  const onCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setCoverBusy(true);
    try { const url = await compressImage(file); setF((p) => ({ ...p, image: url })); }
    catch { toast.error("Couldn't process that image"); }
    setCoverBusy(false);
  };

  const selected = client(f.clientId);
  const householdMembers = selected?.householdId ? clients.filter((c) => c.householdId === selected.householdId) : [];
  const isHousehold = householdMembers.length > 1;
  const [picked, setPicked] = useState(() => new Set([f.clientId]));
  useEffect(() => {
    const sel = clients.find((c) => c.id === f.clientId);
    const hm = sel?.householdId ? clients.filter((c) => c.householdId === sel.householdId) : [];
    const prim = hm.find((c) => c.householdPrimary);
    setPicked(new Set([f.clientId, prim?.id].filter(Boolean)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.clientId]);
  const toggleMember = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const buildTravelers = (c, count) => {
    if (isHousehold) return householdMembers.filter((m) => picked.has(m.id)).map((m) => m.name);
    const names = trip && trip.clientId === c.id ? trip.travelers : [c.name].filter(Boolean);
    return Array.from({ length: count }, (_, i) => names[i] || `Guest ${i + 1}`);
  };

  const saveEdit = () => {
    const c = client(f.clientId);
    if (!f.departure || !f.ret || daysBetween(f.departure, f.ret) < 1) return toast.error("Add a departure and a later return date");
    const nights = Math.max(1, daysBetween(f.departure, f.ret));
    updateTrip(trip.id, { clientId: c.id, title: f.title, bookingNumber: f.bookingNumber.trim(), destination: f.destination, supplier: f.supplier, vessel: f.vessel, kind: f.kind, room: f.room, departure: f.departure, nights, travelers: buildTravelers(c, Math.max(1, +f.travelers)), image: f.image, days: fitDays(trip.days, nights, f.destination), stops: trip.days || nights === trip.nights ? trip.stops : null, port: f.destination });
    toast.success("Trip details updated");
    onOpenChange(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (trip) return saveEdit();
    if (!f.departure || !f.ret || daysBetween(f.departure, f.ret) < 1) return toast.error("Add a departure and a later return date");
    const err = planError(f.plan);
    if (err) return toast.error(err);
    const c = client(f.clientId);
    if (!c) return toast.error("Invite a client first");
    const travelers = buildTravelers(c, Math.max(1, +f.travelers));
    const created = await createQuote({ clientId: c.id, title: f.title, bookingNumber: f.bookingNumber.trim(), destination: f.destination, supplier: f.supplier, vessel: f.vessel, kind: f.kind, room: f.room, departure: f.departure, nights: Math.max(1, daysBetween(f.departure, f.ret)), travelers, image: f.image, fare: 0, items: [], days: [], plan: f.plan, port: f.destination, stops: null });
    if (!created) return;
    toast.success(`Quote ${created.quoteNumber} created`, { description: `${f.title} for ${c.name}` });
    onOpenChange(false);
    nav(`/agent/quotes/${created.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(var(--app-h,100dvh)-2rem)] w-[calc(100vw-1.5rem)] max-w-2xl overflow-y-auto rounded-2xl p-5 sm:p-7" data-testid="new-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-navy-900">{trip ? "Edit Trip Details" : "New Quote"}</DialogTitle>
          <DialogDescription>{trip ? `Update the details for ${trip.bookingNumber || trip.quoteNumber}. Invoice and payment plan are edited on the quote.` : "Enter the trip details and cover photo. You'll add the invoice and itinerary once the quote is created."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 grid gap-4 sm:grid-cols-2">
          <F label="Client" className="sm:col-span-2">
            <select data-testid="quote-client-select" className={field} value={f.clientId} onChange={set("clientId")}>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
          </F>
          <div className="sm:col-span-2">
            <span className="mb-1.5 block text-xs font-medium text-slate-500">Cover photo</span>
            <div className="relative h-40 overflow-hidden rounded-xl border border-slate-200">
              <img src={f.image || DEFAULT_COVER} alt="Trip cover" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-navy-950/25" />
              <input ref={coverRef} type="file" accept="image/*" hidden onChange={onCover} data-testid="quote-cover-input" />
              <button type="button" onClick={() => coverRef.current?.click()} disabled={coverBusy} data-testid="quote-cover-btn" className={cn("absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-navy-900 backdrop-blur transition-colors hover:bg-white", coverBusy && "opacity-60")}>
                <ImageIcon className="h-3.5 w-3.5" />{coverBusy ? "Processing…" : "Change cover photo"}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 sm:col-span-2">
            <F label="Trip name"><input required data-testid="quote-title-input" className={field} value={f.title} onChange={set("title")} /></F>
            <F label="Booking number"><input data-testid="quote-booking-input" className={field} value={f.bookingNumber} onChange={set("bookingNumber")} placeholder="Optional — e.g. CMP-123456" /></F>
          </div>
          <F label="Type">
            <select data-testid="quote-kind-select" className={field} value={f.kind} onChange={set("kind")}><option value="cruise">Cruise</option><option value="resort">Resort stay</option></select>
          </F>
          <F label="Destination"><input required data-testid="quote-destination-input" className={field} value={f.destination} onChange={set("destination")} /></F>
          <F label="Supplier"><input required data-testid="quote-supplier-input" className={field} value={f.supplier} onChange={set("supplier")} /></F>
          <F label={f.kind === "cruise" ? "Ship" : "Resort"}><input required data-testid="quote-vessel-input" className={field} value={f.vessel} onChange={set("vessel")} /></F>
          <F label={f.kind === "cruise" ? "Stateroom type" : "Room type"}><input required data-testid="quote-room-input" className={field} value={f.room} onChange={set("room")} /></F>
          {isHousehold ? (
            <div className="rounded-xl border border-slate-200 p-4 sm:col-span-2" data-testid="quote-household-travelers">
              <p className="mb-2 text-sm font-semibold text-navy-900">Household travelers <span className="font-normal text-slate-400">· primary + selected members</span></p>
              <div className="grid gap-2 sm:grid-cols-2">
                {householdMembers.map((m) => {
                  const forced = m.id === f.clientId;
                  const on = picked.has(m.id);
                  return (
                    <label key={m.id} data-testid={`household-traveler-${m.id}`} className={cn("flex items-center gap-2 rounded-lg border px-3 py-2 text-sm", on ? "border-navy-300 bg-navy-50" : "border-slate-200", forced && "opacity-70")}>
                      <input type="checkbox" checked={on} disabled={forced} onChange={() => toggleMember(m.id)} className="h-4 w-4 rounded border-slate-300 text-navy-900" />
                      <span className="truncate text-navy-900">{m.name}{m.householdPrimary && <span className="ml-1 text-xs text-slate-400">· primary</span>}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            <F label="Travelers"><input required type="number" min="1" max="8" data-testid="quote-travelers-input" className={field} value={f.travelers} onChange={set("travelers")} /></F>
          )}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 sm:col-span-2">
            <F label="Departure date"><input required type="date" data-testid="quote-departure-input" className={field} value={f.departure} onChange={setDeparture} /></F>
            <F label="Return date"><input required type="date" data-testid="quote-return-input" className={field} min={f.departure || undefined} value={f.ret} onChange={set("ret")} /></F>
          </div>
          {!trip && <div className="rounded-xl border border-slate-200 p-4 sm:col-span-2" data-testid="quote-payment-plan">
            <p className="mb-3 text-sm font-semibold text-navy-900">Payment plan <span className="font-normal text-slate-400">· deposit + final payment</span></p>
            <PlanFields plan={f.plan} onChange={(plan) => setF({ ...f, plan })} testPrefix="quote-plan" />
          </div>}
          <div className="flex flex-col-reverse gap-2 pt-2 sm:col-span-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={() => onOpenChange(false)} className={btn.secondary} data-testid="quote-cancel-btn">Cancel</button>
            <button type="submit" className={btn.primary} data-testid="quote-create-submit">{trip ? "Save details" : "Create quote"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
